/**
 * Top-level QC Review panel.
 *
 * Mounted in `RunDetailPanel` below the artefact summary. Orchestrates
 * `useQcLatestRun` + the four mutation hooks and renders the run header,
 * the per-check list, the acknowledge dialog, and the footer with re-run /
 * export controls.
 *
 * The body is split into `<QcPanelBody>` so the orchestrator stays under
 * the eslint max-lines-per-function (75) and complexity (10) budgets.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFormatters } from '../../../../i18n/hooks/use-formatters'
import { useQcLatestRun } from '../../../../hooks/use-qc-runs'


import {
  useAcknowledgeQc,
  useExportQc,
  useRunQc,
} from '../../../../hooks/use-qc-mutations'
import type { QcCheckResult, QcRunWithResults } from '../../../../types/qc'
import { QcAcknowledgeDialog } from './QcAcknowledgeDialog'
import { QcCheckRow } from './QcCheckRow'
import { PanelEmpty, PanelFooter, PanelHeader } from './qc-review-panel.parts'

const ELIGIBLE_RUN_STATUSES = new Set(['completed', 'completed_with_issues'])

interface QcReviewPanelProps {
  caseId: string
  runId: string
  runStatus: string
}

export function QcReviewPanel({
  caseId,
  runId,
  runStatus,
}: QcReviewPanelProps): React.ReactElement {
  const { latest, loading } = useQcLatestRun(caseId, runId)
  const { run: runQc, running, error: runError } = useRunQc()
  const { acknowledge, acknowledging, error: ackError } = useAcknowledgeQc()
  const { exportRun, exporting } = useExportQc()
  const [ackTarget, setAckTarget] = useState<QcCheckResult | null>(null)

  const handleRun = (): void => {
    void runQc(caseId, runId)
  }

  const handleAckSubmit = async (reason: string): Promise<boolean> => {
    if (ackTarget === null || latest === null) return false
    const result = await acknowledge({
      caseId,
      runId,
      qcRunId: latest.id,
      resultId: ackTarget.id,
      reason,
    })
    return result !== null
  }

  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <QcPanelBody
        latest={latest}
        loading={loading}
        running={running}
        exporting={exporting}
        eligible={ELIGIBLE_RUN_STATUSES.has(runStatus)}
        onRun={handleRun}
        onAcknowledge={(target) => {
          setAckTarget(target)
        }}
        onExportCsv={() => {
          if (latest === null) return
          void exportRun({ caseId, runId, qcRunId: latest.id, format: 'csv' })
        }}
        onExportJson={() => {
          if (latest === null) return
          void exportRun({ caseId, runId, qcRunId: latest.id, format: 'json' })
        }}
      />
      {runError !== null && (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400">{runError}</p>
      )}
      <QcAcknowledgeDialog
        open={ackTarget !== null}
        onClose={() => {
          setAckTarget(null)
        }}
        onSubmit={handleAckSubmit}
        submitting={acknowledging}
        error={ackError}
      />
    </div>
  )
}

interface QcPanelBodyProps {
  latest: QcRunWithResults | null
  loading: boolean
  running: boolean
  exporting: boolean
  eligible: boolean
  onRun: () => void
  onAcknowledge: (target: QcCheckResult) => void
  onExportCsv: () => void
  onExportJson: () => void
}

function QcPanelBody(props: QcPanelBodyProps): React.ReactElement {
  const { t } = useTranslation('qc')
  const formatters = useFormatters()
  const unackedCount = useUnackedCount(props.latest)
  const whenRelative = useWhenRelative(props.latest, formatters)

  return (
    <>
      <PanelHeader
        overallStatus={props.latest?.overallStatus ?? null}
        expectedSourceInferred={props.latest?.expectedSource === 'inferred'}
        whenRelative={whenRelative}
        unackedCount={unackedCount}
        t={t}
      />
      {renderBody(props, t)}
    </>
  )
}

function useUnackedCount(latest: QcRunWithResults | null): number {
  return useMemo(() => {
    if (latest === null) return 0
    return latest.results.filter(
      (result) => result.status === 'fail' && result.acknowledgedBy === null
    ).length
  }, [latest])
}

interface FormatRelativeTimeFn {
  formatRelativeTime: (value: Date | string) => string
}

function useWhenRelative(
  latest: QcRunWithResults | null,
  formatters: FormatRelativeTimeFn
): string | null {
  const completedAt = latest?.completedAt ?? null
  if (completedAt === null) return null
  return formatters.formatRelativeTime(completedAt)
}

function renderBody(
  props: QcPanelBodyProps,
  t: ReturnType<typeof useTranslation<'qc'>>['t']
): React.ReactElement {
  if (props.loading) {
    return <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">…</p>
  }
  if (props.latest === null) {
    return (
      <div className="mt-3">
        <PanelEmpty
          onRun={props.onRun}
          running={props.running}
          disabled={!props.eligible}
          disabledReason={t('panel.verifyTooltipBlocked')}
          t={t}
        />
      </div>
    )
  }
  return (
    <QcResultsList
      latest={props.latest}
      running={props.running}
      exporting={props.exporting}
      onRun={props.onRun}
      onAcknowledge={props.onAcknowledge}
      onExportCsv={props.onExportCsv}
      onExportJson={props.onExportJson}
    />
  )
}

interface QcResultsListProps {
  latest: QcRunWithResults
  running: boolean
  exporting: boolean
  onRun: () => void
  onAcknowledge: (target: QcCheckResult) => void
  onExportCsv: () => void
  onExportJson: () => void
}

function QcResultsList(props: QcResultsListProps): React.ReactElement {
  const { t } = useTranslation('qc')
  return (
    <>
      <ul className="mt-3 space-y-2">
        {props.latest.results.map((result) => (
          <QcCheckRow
            key={result.id}
            result={result}
            onAcknowledge={props.onAcknowledge}
          />
        ))}
      </ul>
      <PanelFooter
        onRerun={props.onRun}
        onExportCsv={props.onExportCsv}
        onExportJson={props.onExportJson}
        running={props.running}
        exporting={props.exporting}
        t={t}
      />
    </>
  )
}
